import apiClient from './client';
import { API_ENDPOINTS } from './config';
import type {
	LocationClosure,
	LocationClosureRequest,
	LocationClosureSaveResponse,
	ProviderTimeOffRequest,
	StaffSalonHours,
	StaffTimeOffRequest,
	TimeOffBookingResolveRequest,
	TimeOffListParams,
	TimeOffPreview,
	TimeOffRecord,
	TimeOffResolution,
	TimeOffSaveResponse,
} from '../types/timeOff';

/**
 * Staff time off and salon closures.
 *
 * Only BLOCKED and APPROVED records remove a staff member's labor; the server
 * reads them on every scheduling decision, so nothing here needs a refresh call.
 */
export const timeOffApi = {
	// ─── Staff: own records ─────────────────────────────────────

	/** Own records, soonest first. Withdrawn and removed records are left out. */
	listMine: async (): Promise<TimeOffRecord[]> =>
		apiClient.get<TimeOffRecord[]>(API_ENDPOINTS.TIME_OFF.ME),

	/** What submitting these dates would do — nothing is saved. */
	previewMine: async (data: StaffTimeOffRequest): Promise<TimeOffPreview> =>
		apiClient.post<TimeOffPreview>(API_ENDPOINTS.TIME_OFF.ME_PREVIEW, data),

	/** Block the dates or send them to the provider; the server decides which. */
	createMine: async (data: StaffTimeOffRequest): Promise<TimeOffRecord> =>
		apiClient.post<TimeOffRecord>(API_ENDPOINTS.TIME_OFF.ME, data),

	withdrawMine: async (id: number): Promise<TimeOffRecord> =>
		apiClient.post<TimeOffRecord>(API_ENDPOINTS.TIME_OFF.ME_WITHDRAW(id)),

	removeMine: async (id: number): Promise<TimeOffRecord> =>
		apiClient.delete<TimeOffRecord>(API_ENDPOINTS.TIME_OFF.ME_DETAIL(id)),

	/** Read-only salon hours and closures at each of the staff member's locations. */
	mySalonHours: async (): Promise<StaffSalonHours> =>
		apiClient.get<StaffSalonHours>(API_ENDPOINTS.STAFF.SALON_HOURS),

	// ─── Provider ───────────────────────────────────────────────

	list: async (params: TimeOffListParams = {}): Promise<TimeOffRecord[]> => {
		const query = new URLSearchParams();
		if (params.staffId != null) query.append('staffId', String(params.staffId));
		if (params.locationId != null) query.append('locationId', String(params.locationId));
		if (params.upcomingOnly != null) query.append('upcomingOnly', String(params.upcomingOnly));
		if (params.status) query.append('status', params.status);
		const suffix = query.toString();
		return apiClient.get<TimeOffRecord[]>(
			suffix ? `${API_ENDPOINTS.TIME_OFF.BASE}?${suffix}` : API_ENDPOINTS.TIME_OFF.BASE
		);
	},

	get: async (id: number): Promise<TimeOffRecord> =>
		apiClient.get<TimeOffRecord>(API_ENDPOINTS.TIME_OFF.DETAIL(id)),

	/** Record an absence that takes effect at once. */
	create: async (data: ProviderTimeOffRequest): Promise<TimeOffSaveResponse> =>
		apiClient.post<TimeOffSaveResponse>(API_ENDPOINTS.TIME_OFF.BASE, data),

	update: async (id: number, data: ProviderTimeOffRequest): Promise<TimeOffSaveResponse> =>
		apiClient.put<TimeOffSaveResponse>(API_ENDPOINTS.TIME_OFF.DETAIL(id), data),

	/** Removes it from the schedule; the record stays on file as REMOVED. */
	remove: async (id: number): Promise<void> =>
		apiClient.delete<void>(API_ENDPOINTS.TIME_OFF.DETAIL(id)),

	approve: async (id: number, note?: string): Promise<TimeOffRecord> =>
		apiClient.post<TimeOffRecord>(API_ENDPOINTS.TIME_OFF.APPROVE(id), {
			note: note ?? null,
		}),

	/** A note is required. */
	decline: async (id: number, note: string): Promise<TimeOffRecord> =>
		apiClient.post<TimeOffRecord>(API_ENDPOINTS.TIME_OFF.DECLINE(id), { note }),

	/** Where every booking inside a pending request stands, and who could take each over. */
	resolution: async (id: number): Promise<TimeOffResolution> =>
		apiClient.get<TimeOffResolution>(API_ENDPOINTS.TIME_OFF.RESOLUTION(id)),

	/**
	 * Reschedule, reassign, cancel or refund one affected booking. Same rules as the
	 * Appointments page; conflicts and Stripe refusals come back as errors with a message.
	 */
	resolveBooking: async (
		id: number,
		appointmentId: number,
		data: TimeOffBookingResolveRequest
	): Promise<TimeOffResolution> =>
		apiClient.post<TimeOffResolution>(
			API_ENDPOINTS.TIME_OFF.RESOLVE_BOOKING(id, appointmentId),
			data
		),

	// ─── Salon-wide closures (provider) ─────────────────────────

	listClosures: async (locationId: number, includePast = false): Promise<LocationClosure[]> =>
		apiClient.get<LocationClosure[]>(
			`${API_ENDPOINTS.LOCATION_CLOSURES(locationId)}?includePast=${includePast}`
		),

	createClosure: async (
		locationId: number,
		data: LocationClosureRequest
	): Promise<LocationClosureSaveResponse> =>
		apiClient.post<LocationClosureSaveResponse>(
			API_ENDPOINTS.LOCATION_CLOSURES(locationId),
			data
		),

	updateClosure: async (
		closureId: number,
		data: LocationClosureRequest
	): Promise<LocationClosureSaveResponse> =>
		apiClient.put<LocationClosureSaveResponse>(
			API_ENDPOINTS.LOCATION_CLOSURE_DETAIL(closureId),
			data
		),

	deleteClosure: async (closureId: number): Promise<void> =>
		apiClient.delete<void>(API_ENDPOINTS.LOCATION_CLOSURE_DETAIL(closureId)),
};

export default timeOffApi;
